import { useEffect, useState } from 'react';
import type { RootState } from '../../../../store';
import { useSelector } from 'react-redux';
import type { Medication } from '../../../../lib/transferObjects/Medication';
import { mapToMedicationDetails } from '../../../components/Users/OrderSetForm/helper';

interface InitialMed {
  id: string | number;
  amt?: {
    mpId?: string | number;
    mpuuId?: string | number;
    tpuuId?: string | number;
  };
  med_name: string;
  further_information?: string | null;
  regimen?: {
    isPrn?: boolean;
    prn?: {
      prnNote?: string;
    };
  };
}
export const useInitialMeds = () => {
  const { rawEvents, latestEpisodeId } = useSelector(
    (state: RootState) => state.timeline,
  );
  const [initialMeds, setInitialMeds] = useState<Medication[]>([]);
  const requiredOrderStatusCode = 'AUA1';
  useEffect(() => {
    const meds = extractMedicationRequests(rawEvents, latestEpisodeId);
    setInitialMeds(meds.map(mapToMedicationDetails));
  }, [rawEvents, latestEpisodeId]);

  const extractMedicationRequests = (
    data: any,
    episodeId?: string | number | null,
  ) => {
    const medMap = new Map<
      string | number,
      { med: InitialMed; eventDate: string }
    >();

    if (!data || typeof data !== 'object') return [];

    const eventArrays: any[][] = [];

    const traverse = (node: any): void => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach((child) => traverse(child));
        return;
      }
      if (typeof node === 'object') {
        if (Array.isArray(node.events)) {
          eventArrays.push(node.events);
        }
        for (const val of Object.values(node)) {
          traverse(val);
        }
      }
    };

    traverse(data);
    for (const events of eventArrays) {
      for (const ev of events) {
        if (
          episodeId !== undefined &&
          episodeId !== null &&
          String(ev?.episode_id ?? '') !== String(episodeId)
        ) {
          continue;
        }

        const mpId: string = ev?.detail?.mp_id;
        const fhir = ev?.detail?.fhir;
        const medId = ev?.id ?? 0;
        const eventDate = ev?.event_date ?? '';
        if (!fhir || !mpId) continue;

        const isCompleted =
          fhir?.status === 'completed' || fhir?.status === 'Active';
        const isEmrOrder = ev?.detail?.emrOrder === true;
        const isHomeMedication = ev?.detail?.homeMedication === true;
        if (!(isCompleted && (isEmrOrder || isHomeMedication))) {
          continue;
        }

        const status: string = String(
          ev?.detail?.orderstatuscode ?? ev?.detail?.orderStatusCode ?? '',
        );
        if (status !== requiredOrderStatusCode) {
          continue;
        }

        const isMedicationRequest =
          fhir?.resourceType === 'MedicationRequest' ||
          (Array.isArray(fhir?.contained) &&
            fhir.contained.some(
              (c: any) => c?.resourceType === 'MedicationRequest',
            ));
        if (!isMedicationRequest) continue;

        const name: string = fhir?.name ?? ev?.event_name ?? '';
        const dosageInstruction = fhir?.dosageInstruction?.text ?? null;

        const med: InitialMed = {
          med_name: name,
          further_information: dosageInstruction,
          regimen: {
            isPrn: dosageInstruction ? true : false,
            prn: {
              prnNote: dosageInstruction,
            },
          },
          id: medId,
          amt: {
            mpId,
          },
        };

        // Only keep the most recent medication for each mpId
        const existing = medMap.get(mpId);
        if (!existing || eventDate > existing.eventDate) {
          medMap.set(mpId, { med, eventDate });
        }
      }
    }

    const results: Array<InitialMed> = Array.from(medMap.values()).map(
      ({ med }) => med,
    );

    results.sort((a, b) => a.med_name.localeCompare(b.med_name));

    return results;
  };

  return {
    initialMeds,
  };
};
